import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { makeStyles, tokens } from "@fluentui/react-components";
import { ArrowClockwise16Regular, ArrowClockwise20Regular } from "@fluentui/react-icons";
import { Link } from "@fluentui/react-components";
import { SingleSelectTagPicker, TagData } from "../../_base/SingleSelectTagPicker";
import { useFilteredProjects } from "../../../hooks/useFilteredProjects";
import { useSuggestedProjects } from "../../../hooks/useSuggestedProjects";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { IProjectData } from "../../../models/shared/IProjectData";
import { Logger } from "../../../services/Logger";
import { SmartFilingManagerFluent9 } from "../../../services/SmartFiling/SmartFilingManagerFluent9";
import { MailboxItem } from "../../../services/OfficeWrapper";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
    },
    container: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    subfolderContent: {
        display: "inline-flex",
        alignItems: "center",
    },
    link: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        marginLeft: "auto",
        fontSize: tokens.fontSizeBase200,
    },
});

export interface FilteredProjectPickerFluent9Props extends LocalizeContextProps {
    logger: Logger;
    projectsService: IProjectsService;
    smartFilingManager: SmartFilingManagerFluent9;
    mailboxItem: MailboxItem | null;
    disabled: boolean;
    selectedProject?: IProjectData;
    onProjectSelected: (project?: IProjectData) => void;
    myProjects: IProjectData[];
    isLoadingMyProjects: boolean;
    showAllGlobalProjects: boolean;
    /** Whether to display the "All Projects" group header in the dropdown. Global project search is still supported regardless. Defaults to true. */
    showGlobalSearchResults?: boolean;
    reloadToken?: number;
    showValidationError?: boolean;
    onReloadProjects?: () => void;
    reloadIconSize?: 16 | 20;
    subfolderToggleContent?: React.ReactNode;
    /** Rendered full-width below the subfolder toggle / reload projects row (e.g. the subfolder picker field). */
    subfolderPickerContent?: React.ReactNode;
}

const SUGGESTED_GROUP_KEY = "suggested";
const MY_PROJECTS_GROUP_KEY = "my";
const GLOBAL_GROUP_KEY = "global";
const MY_PROJECTS_DISPLAY_LIMIT = 25;
const MAX_DROPDOWN_ITEMS_INITIAL = MY_PROJECTS_DISPLAY_LIMIT + 3;
const MAX_DROPDOWN_ITEMS_FILTERED = 25;

export const FilteredProjectPickerFluent9 = withLocalize((props: FilteredProjectPickerFluent9Props) => {
    const styles = useStyles();
    const ReloadIcon = props.reloadIconSize === 16 ? ArrowClockwise16Regular : ArrowClockwise20Regular;
    const translationsReady = !!props.activeLanguage;
    const [query, setQuery] = useState("");
    const allProjectsRef = useRef<IProjectData[]>([]);

    const { searchResults, isSearching, setQuery: setSearchQuery } = useFilteredProjects({
        projectsService: props.projectsService,
        debounceMs: 500,
        maxServerResults: MAX_DROPDOWN_ITEMS_FILTERED,
        autoPrime: props.showAllGlobalProjects,
    });

    const { suggestedProjects } = useSuggestedProjects({
        smartFilingManager: props.smartFilingManager,
        myProjects: props.myProjects,
        logger: props.logger,
        reloadToken: props.reloadToken,
    });

    // Auto-select first suggested project
    const autoSelectedRef = useRef(false);

    // Reset auto-select on reload
    useEffect(() => {
        if (props.reloadToken !== undefined && props.reloadToken > 0) {
            autoSelectedRef.current = false;
        }
    }, [props.reloadToken]);

    useEffect(() => {
        if (autoSelectedRef.current || props.selectedProject) return;
        if (suggestedProjects.length > 0) {
            props.onProjectSelected(suggestedProjects[0]);
            autoSelectedRef.current = true;
        }
    }, [suggestedProjects]);

    const suggestedLabel = props.translate("SHARED.SUGGESTED_PROJECTS.SUGGESTED_PROJECTS_TITLE").toString();
    const myProjectsLabel = props.translate("SHARED.MY_PROJECTS").toString();
    const globalProjectsLabel = props.translate("SEND_AND_FILE_NEW.ALL_PROJECTS").toString();

    // Compute options dynamically based on query
    const options = useMemo(() => {
        const trimmedLower = query.trim().toLowerCase();
        const result: TagData[] = [];

        // Suggested projects (always shown, client-filtered)
        const filteredSuggested = trimmedLower
            ? suggestedProjects.filter((p) => p.displayName.toLowerCase().includes(trimmedLower))
            : suggestedProjects;

        filteredSuggested.forEach((p) => {
            result.push({
                key: p.nrn,
                label: p.displayName,
                group: { key: SUGGESTED_GROUP_KEY, label: suggestedLabel },
            });
        });

        // My projects (client-filtered, augmented by server results when available)
        const suggestedNrns = new Set(suggestedProjects.map((p) => p.nrn));
        const myProjectNrns = new Set(props.myProjects.map((p) => p.nrn));

        // Start with client-filtered my projects
        const clientFilteredMyProjects = (trimmedLower
            ? props.myProjects.filter((p) => p.displayName.toLowerCase().includes(trimmedLower))
            : props.myProjects
        ).filter((p) => !suggestedNrns.has(p.nrn));

        // When server results are available, also include any server-matched my-projects
        // that the client-side filter may have missed (e.g., matched by project number)
        const hasServerResults = props.showAllGlobalProjects && trimmedLower.length >= 3 && searchResults.length > 0;
        const clientFilteredNrns = new Set(clientFilteredMyProjects.map((p) => p.nrn));
        const serverMatchedMyProjects = hasServerResults
            ? searchResults.filter(
                  (p) => myProjectNrns.has(p.nrn) && !suggestedNrns.has(p.nrn) && !clientFilteredNrns.has(p.nrn)
              )
            : [];

        const allMyProjectResults = [...clientFilteredMyProjects, ...serverMatchedMyProjects];
        allMyProjectResults.slice(0, MY_PROJECTS_DISPLAY_LIMIT).forEach((p) => {
            result.push({
                key: p.nrn,
                label: p.displayName,
                group: { key: MY_PROJECTS_GROUP_KEY, label: myProjectsLabel },
            });
        });

        // "All Projects" group — only non-my-project server results
        const showGlobal = props.showGlobalSearchResults !== false;
        if (showGlobal && hasServerResults) {
            const existingNrns = new Set(result.map((r) => r.key));
            searchResults
                .filter((p) => !existingNrns.has(p.nrn) && !myProjectNrns.has(p.nrn))
                .forEach((p) => {
                    result.push({
                        key: p.nrn,
                        label: p.displayName,
                        group: { key: GLOBAL_GROUP_KEY, label: globalProjectsLabel },
                    });
                });
        }

        const limit = trimmedLower ? MAX_DROPDOWN_ITEMS_FILTERED : MAX_DROPDOWN_ITEMS_INITIAL;
        return result.slice(0, limit);
    }, [
        query,
        suggestedProjects,
        props.myProjects,
        props.showAllGlobalProjects,
        searchResults,
        suggestedLabel,
        myProjectsLabel,
        globalProjectsLabel,
    ]);

    const selectedPickerOption = useMemo(() => {
        if (props.selectedProject) {
            return {
                key: props.selectedProject.nrn,
                label: props.selectedProject.displayName,
            };
        }
        return undefined;
    }, [props.selectedProject]);

    const handleQueryChange = useCallback(
        (value: string) => {
            setQuery(value);
            if (props.showAllGlobalProjects) {
                setSearchQuery(value);
            }
        },
        [setSearchQuery, props.showAllGlobalProjects]
    );

    // Keep a stable ref of all available projects for the selection callback (synchronous to avoid stale reads)
    const allProjectsSnapshot = useMemo(() => [...suggestedProjects, ...props.myProjects, ...searchResults], [
        suggestedProjects,
        props.myProjects,
        searchResults,
    ]);
    allProjectsRef.current = allProjectsSnapshot;

    const onOptionSelect = useCallback(
        (data?: TagData) => {
            if (!data) {
                // Reset query and search results so dropdown shows initial state on next open
                setQuery("");
                setSearchQuery("");
                props.onProjectSelected(undefined);
                return;
            }
            // Find the project from all available sources using ref for stable lookup
            const found = allProjectsRef.current.find((p) => p.nrn === data.key);
            props.onProjectSelected(found);
        },
        [props.onProjectSelected, setSearchQuery]
    );

    const placeholder = useMemo(() => {
        if (props.isLoadingMyProjects) {
            return props.translate("SEND_AND_FILE_NEW.LOADING_PROJECTS").toString();
        }
        if (isSearching) {
            return props.translate("APP.LOADING_PROJECTS").toString();
        }
        return props.showAllGlobalProjects
            ? props.translate("SEND_AND_FILE_NEW.SEARCH_PROJECTS").toString()
            : props.translate("SEND_AND_FILE_NEW.SEARCH_MYPROJECTS").toString();
    }, [props.isLoadingMyProjects, isSearching, props.showAllGlobalProjects, props.translate]);

    const statusMessage = useMemo(() => {
        if (!props.showAllGlobalProjects) return undefined;
        const trimmed = query.trim();
        if (trimmed.length >= 1 && trimmed.length <= 2) {
            return props.translate("SHARED.SUGGESTED_PROJECTS.KEEP_TYPING_HINT").toString();
        }
        if (trimmed.length >= 3 && isSearching) {
            return props.translate("SHARED.SUGGESTED_PROJECTS.SEARCHING_ALL_PROJECTS").toString();
        }
        return undefined;
    }, [query, isSearching, props.showAllGlobalProjects, props.translate]);

    const headerMessage = useMemo(() => {
        if (query.trim().length > 0) return undefined;
        const showHint = props.showAllGlobalProjects || props.myProjects.length > MY_PROJECTS_DISPLAY_LIMIT;
        if (!showHint) return undefined;
        return props.translate("FILE_MULTIPLE_EMAIL.USE_SEARCH").toString();
    }, [query, props.showAllGlobalProjects, props.myProjects.length, props.translate]);

    if (!translationsReady) {
        return <div className={styles.root} data-testid="filtered-project-picker-container" />;
    }

    return (
        <div className={styles.root} data-testid="filtered-project-picker-container">
            <SingleSelectTagPicker
                label={props.translate("SHARED.SUGGESTED_PROJECTS.LABEL").toString()}
                name="filtered-project"
                options={options}
                selectedOption={selectedPickerOption}
                required
                disabled={props.disabled || props.isLoadingMyProjects}
                isLoading={isSearching && query.trim().length >= 3}
                statusMessage={statusMessage}
                keepQueryOnClose={true}
                limit={query.trim() ? MAX_DROPDOWN_ITEMS_FILTERED : MAX_DROPDOWN_ITEMS_INITIAL}
                headerMessage={headerMessage}
                placeholder={placeholder}
                noOptionsLabel={props.translate("SHARED.SUGGESTED_PROJECTS.NO_PROJECTS").toString()}
                hideNoOptions={
                    props.showAllGlobalProjects && query.trim().length >= 1 && (query.trim().length < 3 || isSearching)
                }
                limitExceededLabel={props.translate("SHARED.FIELD.PROJECT_LIMIT_EXCEEDED.LABEL").toString()}
                onOptionSelect={onOptionSelect}
                onQueryChange={handleQueryChange}
                validationMessage={
                    props.showValidationError ? props.translate("SHARED.FIELD.REQUIRED_ERROR").toString() : undefined
                }
            />
            {(props.subfolderToggleContent || !!props.onReloadProjects) && (
                <div className={styles.container}>
                    {props.subfolderToggleContent ? (
                        <div className={styles.subfolderContent}>{props.subfolderToggleContent}</div>
                    ) : null}
                    {props.onReloadProjects ? (
                        <Link
                            data-testid="filtered-picker-reload-projects-button"
                            onClick={props.onReloadProjects}
                            className={styles.link}
                            disabled={props.isLoadingMyProjects}
                        >
                            <ReloadIcon />
                            {props.translate("SEND_AND_FILE_NEW.RELOAD_PROJECTS")}
                        </Link>
                    ) : null}
                </div>
            )}
            {props.subfolderPickerContent ?? null}
        </div>
    );
});

export default FilteredProjectPickerFluent9;
